import { UserService } from './userService';
import { Service } from './service';
import { PublicService } from './publicService';
import { NetworkService } from './networkService';
import { ForumsService } from './forumsService';
import { BlogsService } from './blogsService';
import { ActivityService } from './activityService';
import { MediaService } from './mediaService';
import { NotificationsService } from './notificationsService';
import { CommunityService } from './communityService';
import { Database } from './database';
import { ChatService } from './chatService';
import { MonitorService } from './monitorService';

export class Server {
  public static user: UserService = new UserService();
  public static public: PublicService = new PublicService();
  public static forums: ForumsService = new ForumsService();
  public static blogs: BlogsService = new BlogsService();
  public static activity: ActivityService = new ActivityService();
  public static media: MediaService = new MediaService();
  public static network: NetworkService = new NetworkService();
  public static notifications: NotificationsService = new NotificationsService();
  public static community: CommunityService = new CommunityService();
  public static database: Database = new Database();
  public static chat: ChatService = new ChatService();
  public static monitor: MonitorService = new MonitorService();
  public static initialized: boolean = false;
  public static synchronizing: boolean = false;
  
  protected static communityId: string = null;
  protected static loggingIn: boolean = false;
  protected static loggedIn: boolean = false;
  protected static listeners:any[] = [];

  public static async init(community:string = null) {
    this.communityId = community;

    if(community) {
      Service.setCommunity(community);
      Server.network.setCommunity(community);
    }

    await Server.database.init();

    let promises = [];
    promises.push(Server.user.init());

    if(community) {
      promises.push(Server.notifications.init());
      promises.push(Server.activity.init());
      promises.push(Server.forums.init());
      promises.push(Server.public.init());
      promises.push(Server.chat.init());
      promises.push(Server.monitor.init());
    }

    let responses = await Promise.all(promises);

    for(let i = 0; i < responses.length; i++)
      if(!responses[i].success)
        return responses[i];

    Server.initialized = true;
  }

  public static getEnvironment() {
    return Service.getEnvironment();
  }

  public static isLoggingIn():boolean {
    return Server.loggingIn;
  }

  public static isLoggedIn(): boolean {
    return Server.loggedIn;
  }

  private static randomValues(length: number): string {
    const values = crypto.getRandomValues(new Uint8Array(length));
    return Array.from(values).map(b => b.toString(16).padStart(2, '0')).join('')
  }

  private static base64(data:string) {
    return btoa(data).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  }

  private static async sha256(input: string) {
    const encoded = new TextEncoder().encode(input);
    const digest = await crypto.subtle.digest("SHA-256", encoded);
    const bytes = new Uint8Array(digest);
    let data = '';
    for (let i = 0; i < bytes.length; i++) 
      data += String.fromCharCode(bytes[i]);
    return this.base64(data);
  }

  public static async login(signup:boolean = false) {
    const nonce = Server.randomValues(16);
    const verifier = Server.randomValues(64);
    const challenge = await Server.sha256(verifier);

    const returnTo = window.location.origin;
    const stateStr = JSON.stringify({n: nonce, r: returnTo, v: verifier});
    const state = Server.base64(stateStr);

    const q = new URLSearchParams({
      response_type: 'code',
      client_id: '5mra5m4aa966ghpkhk8kb9p0eh',
      redirect_uri: 'https://api.oneclick.community/cognito/login/v1',
      scope: 'openid email aws.cognito.signin.user.admin',
      code_challenge: challenge,
      code_challenge_method: 'S256',
      state,
      nonce
    });

    let redirectPath = signup ? '/signup' : '/oauth2/authorize';

    window.location.assign(`https://auth.oneclick.community/${redirectPath}?${q.toString()}`);
  }

  public static async sync() {
    Server.synchronizing = true;
    let response = await Server.user.sync();  
    Server.synchronizing = false;

    if(!response.success) {
      // await Server.logout();
      return response;
    }

    let community = Service.getCommunity();

    if(community != '') {
      Server.network.setCommunity(community);
      Server.network.connect();
    }
  
    Server.notifyListeners('login');

    this.monitor.start();

    Server.loggingIn = false;
    Server.loggedIn = true;

    return {success: true};
  }

  public static async logout() {
    this.monitor.stop();
    await Server.database.purgeAll();
    Server.user.clear();
    Server.network.disconnect();
    window.sessionStorage.clear();
    Server.notifyListeners('logout');
    Server.loggingIn = false;
    Server.loggedIn = false;
    window.location.assign(`https://api.oneclick.community/cognito/logout/v1?next=${encodeURIComponent(window.location.origin)}`);
  }


  public static addEventListener(id:string, callback:Function) {
    Server.listeners.push({id, callback});
  }

  public static removeEventListener(id:string, callback:Function) {
    for(let i = 0; i < Server.listeners.length; i++) {
      if(Server.listeners[i].id == id && Server.listeners[i].callback == callback) {
        Server.listeners.splice(i, 1);
        return;
      }
    }
  }

  public static notifyListeners(eventId:string, eventData:any = {}) {
    for(let i = 0; i < Server.listeners.length; i++) 
      if(Server.listeners[i].id == eventId) 
        Server.listeners[i].callback(eventData);
  }
}
